<style lang="scss">
.permission-microphone {
  &__mic,
  &__frequency-bars {
    border-radius: 20px;
  }

  &__mic {
    background-image: linear-gradient(
      to bottom,
      #959595 0%,
      #0d0d0d 46%,
      #010101 50%,
      #0a0a0a 53%,
      #4e4e4e 76%,
      #383838 87%,
      #1b1b1b 100%
    );
  }

  &__icon {
    position: absolute;
    top: calc(50% - 48px);
    left: calc(50% - 21px);
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    width: 42px;
    height: 72px;
    padding-bottom: 10px;
    border-radius: 16px;
    background-color: #fff;
    transition: background-color 0.3s;

    &.--shake {
      animation: shake 0.2s linear 3;
    }

    &-line {
      width: 25px;
      height: 4px;
      margin-bottom: 3px;
      background: #222;
      border-radius: 2px 2px 4px 4px;
    }

    &-stem {
      position: absolute;
      bottom: -15px;
      left: calc(50% - 4px);
      width: 8px;
      height: 16px;
      background-color: #fff;
      transition: background-color 0.3s;
    }

    &-base {
      position: absolute;
      bottom: -21px;
      left: calc(50% - 15px);
      width: 30px;
      height: 6px;
      border-radius: 2px;
      background-color: #fff;
      transition: background-color 0.3s;
    }
  }

  &__mic.--is-recording {
    .permission-microphone__icon,
    .permission-microphone__icon-stem,
    .permission-microphone__icon-base {
      background-color: #d32f2f;
    }
  }
}

@keyframes shake {
  0% {
    transform-origin: bottom center;
    transform: rotate(-5deg);
  }
  50% {
    transform-origin: bottom center;
    transform: rotate(5deg);
  }
  100% {
    transform-origin: bottom center;
    transform: rotate(0deg);
  }
}
</style>

<template>
  <div class="pa-5 permission-microphone">
    <div class="text-xs-center">
      <p class="mb-5 display-4 font-weight-regular">
        <span class="d-inline-block red--text text--lighten-2 --attention">
          {{ isPermission ? '踩踏板' : '允許' }}
        </span>
        {{ isPermission ? '錄音' : '使用麥克風' }}
      </p>

      <v-card
        width="200"
        height="200"
        :class="[
          'mx-auto',
          'permission-microphone__mic',
          { '--is-recording': isRecording },
        ]"
      >
        <canvas
          ref="canvas"
          width="200"
          height="200"
          class="permission-microphone__frequency-bars"
        />
        <div
          :class="['permission-microphone__icon', { '--shake': !isRecording }]"
        >
          <div class="permission-microphone__icon-line" />
          <div class="permission-microphone__icon-line" />
          <div class="permission-microphone__icon-line" />
          <div class="permission-microphone__icon-stem" />
          <div class="permission-microphone__icon-base" />
        </div>
      </v-card>

      <v-card height="50" flat class="my-4">
        <audio :src="audioSrc" controls />
      </v-card>

      <v-card min-height="150" flat>
        <v-card-text class="success--text display-4 font-weight-regular">
          {{ voice }}
        </v-card-text>
      </v-card>
    </div>

    <faq-switch-drawer title="麥克風" :items="faqs" />
  </div>
</template>

<script>
import { throttle } from 'lodash'

import FaqSwitchDrawer from './faq-drawer'

const KEY_Z = 90

export default {
  name: 'PermissionMicrophone',
  components: {
    FaqSwitchDrawer,
  },
  data() {
    return {
      isPermission: false,
      recorder: null,
      isRecording: false,
      audioSrc: '',
      voice: '',
      analyser: null,
      raf: 0,
      device: '未取得授權',
      stream: null,
    }
  },
  computed: {
    canvas() {
      return this.$refs.canvas
    },
    context2d() {
      if (this.$refs.canvas) {
        return this.$refs.canvas.getContext('2d')
      }
      return null
    },
    faqs() {
      return [
        {
          id: 'mic-1',
          text: '瀏覽器網址列內最右側是否有出現攝影或麥克風圖案 ',
          icons: ['videocam', 'mic'],
        },
        {
          id: 'mic-2',
          text: `是否選擇正確的音源，目前為: ${this.device}。`,
        },
        {
          id: 'mic-3',
          text: '確認 chrome://flags/#unsafely-treat-insecure-origin-as-secure 是否包含正確的服務路徑。',
          href: 'chrome://flags/#unsafely-treat-insecure-origin-as-secure',
        },
        {
          id: 'mic-4',
          text: '在 windows 麥克風設定 "檢查收音音量" 與 "作業系統" 使否有偵測到該麥克風。',
        },
      ]
    },
  },
  created() {
    this.$options.sockets.onmessage = this.onMsg
  },
  mounted() {
    setTimeout(() => {
      navigator.mediaDevices
        .getUserMedia({ audio: true, video: false })
        .then(this.success)
        .catch(this.error)
    }, 2000)
  },
  beforeDestroy() {
    removeEventListener('keydown', this.throttleKeydown)
    removeEventListener('keyup', this.throttleKeyup)

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
    }
  },
  methods: {
    startAnimate() {
      if (this.context2d) {
        this.context2d.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.analyser.getByteFrequencyData(this.dataArray)
        this.drawVisualiser()
        this.raf = requestAnimationFrame(this.startAnimate)
      }
    },
    stopAnimate() {
      if (this.context2d) {
        cancelAnimationFrame(this.raf)
        this.context2d.clearRect(0, 0, this.canvas.width, this.canvas.height)
      }
    },
    drawVisualiser() {
      if (this.context2d) {
        this.dataArray.forEach((barHeight, index) => {
          const h = 176
          const sl =
            ((this.canvas.height - barHeight) / this.canvas.height) * 100
          this.context2d.fillStyle = `hsl(${h}, ${sl}%, ${sl}%)`
          this.context2d.fillRect(
            index * this.barWidth,
            this.canvas.height - barHeight,
            this.barWidth,
            barHeight,
          )
          this.context2d.fillRect(
            this.canvas.width - (index + 1) * this.barWidth,
            this.canvas.height - barHeight,
            this.barWidth,
            barHeight,
          )
        })
      }
    },
    parseData(file) {
      const result = JSON.parse(file.target.result)
      const [voice] = result.data
      console.log('SPEECH RECOGNITION =>', voice.text)
      if (voice.text) {
        this.voice = voice.text
        this.$emit('notify')
        this.$emit('valid', { step: 3, valid: true })
      } else {
        this.voice = 'TRY AGAIN'
      }
    },
    onMsg(msg) {
      const reader = new FileReader()
      reader.addEventListener('loadend', this.parseData)
      reader.readAsText(msg.data)
    },
    registerKeyZ() {
      this.throttleKeydown = throttle(this.keydown, 1000, {
        leading: true,
        trailing: false,
      })
      this.throttleKeyup = throttle(this.keyup, 1000, {
        leading: false,
        trailing: true,
      })
      addEventListener('keydown', this.throttleKeydown)
      addEventListener('keyup', this.throttleKeyup)
    },
    success(stream) {
      this.stream = stream
      this.initRecorder(stream)
      this.device = stream.getTracks()[0].label

      setTimeout(() => {
        this.$emit('notify')
      }, 2000)
      setTimeout(() => {
        this.isPermission = true
        this.registerKeyZ()
      }, 3000)
    },
    error(error) {
      console.warn('[ERROR] getUserMedia:', error.message, error.name)
      this.$emit('valid', { step: 3, valid: false })
    },
    initRecorder(stream) {
      const audioCxt = new AudioContext({ sampleRate: 16000 })
      const microphone = audioCxt.createMediaStreamSource(stream)
      this.analyser = audioCxt.createAnalyser()
      microphone.connect(this.analyser)
      this.analyser.fftSize = 128
      const bufferLength = this.analyser.frequencyBinCount
      this.dataArray = new Uint8Array(bufferLength)
      this.barWidth = this.canvas.width / 2 / bufferLength
      this.recorder = new Recorder(microphone)
    },
    setAudioSrc(blob) {
      this.audioSrc = URL.createObjectURL(blob)
    },
    sendToService(blob) {
      if (this.$socket.readyState === 1) {
        this.$socket.send(blob)
        this.$socket.send(new Uint8Array(0))
      } else {
        this.$emit('valid', { step: 3, valid: false })
      }
      this.recorder.clear()
    },
    startRecord() {
      if (!this.isRecording && this.recorder) {
        console.log('[START] RECORD')
        this.isRecording = true
        this.startAnimate()
        this.recorder.record()
        this.$connect()
      }
    },
    stopRecord() {
      if (this.isRecording && this.recorder) {
        console.log('[STOP] RECORD')
        this.isRecording = false
        this.stopAnimate()
        this.recorder.stop()
        this.recorder.exportWAV(this.setAudioSrc)
        this.recorder.export16kMono(this.sendToService, 'audio/x-raw')
      }
    },
    keydown(evt) {
      if (evt.keyCode === KEY_Z) {
        this.startRecord()
      }
    },
    keyup(evt) {
      if (evt.keyCode === KEY_Z) {
        this.stopRecord()
      }
    },
  },
}
</script>
